import { Link, useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  Calendar,
  Building2,
  Loader2,
  Pencil,
  Trash2,
  Hotel,
  Bus,
  Plane,
  Users,
  AlertCircle,
  Clock,
  Package,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { AllocationModal } from '@/components/travel/allocations/allocation-modal';
import { trpc } from '@/lib/trpc';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';
import { format } from 'date-fns';
import { useState } from 'react';

type AllocationType = 'HOTEL_ROOM' | 'TOUR_SEAT' | 'TRANSFER_VEHICLE' | 'FLIGHT_SEAT';
type AllocationStatus = 'ACTIVE' | 'FULLY_CONSUMED' | 'EXPIRED' | 'CANCELLED' | 'SUSPENDED';

const typeIcons: Record<AllocationType, typeof Hotel> = {
  HOTEL_ROOM: Hotel,
  TOUR_SEAT: Users,
  TRANSFER_VEHICLE: Bus,
  FLIGHT_SEAT: Plane,
};

const typeColors: Record<AllocationType, string> = {
  HOTEL_ROOM: 'bg-blue-100 text-blue-700',
  TOUR_SEAT: 'bg-purple-100 text-purple-700',
  TRANSFER_VEHICLE: 'bg-green-100 text-green-700',
  FLIGHT_SEAT: 'bg-sky-100 text-sky-700',
};

const statusColors: Record<AllocationStatus, string> = {
  ACTIVE: 'bg-green-100 text-green-700',
  FULLY_CONSUMED: 'bg-orange-100 text-orange-700',
  EXPIRED: 'bg-gray-100 text-gray-700',
  CANCELLED: 'bg-red-100 text-red-700',
  SUSPENDED: 'bg-yellow-100 text-yellow-700',
};

export function AllocationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('allocations');
  const { t: tCommon } = useTranslation('common');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const { data: allocation, isLoading, error } = trpc.allocation.byId.useQuery(id!, {
    enabled: !!id,
  });

  const utils = trpc.useUtils();

  const deleteMutation = trpc.allocation.delete.useMutation({
    onSuccess: () => {
      showSuccessToast(t('messages.deleted'));
      navigate('/travel/allocations');
    },
    onError: (error) => {
      showErrorToast(error.message || t('messages.deleteError'));
    },
  });

  const confirmDelete = () => {
    if (id) {
      deleteMutation.mutate(id);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2 text-muted-foreground">{t('loading')}</span>
      </div>
    );
  }

  if (error || !allocation) {
    return (
      <div className="border border-red-200 bg-red-50 p-6 text-red-700">
        <div className="flex items-center gap-2">
          <AlertCircle className="h-5 w-5" />
          <p className="font-medium">{t('errorLoading')}</p>
        </div>
        <p className="mt-2 text-sm">{error?.message || t('notFound')}</p>
        <Button variant="outline" className="mt-4" asChild>
          <Link to="/travel/allocations">
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('backToList')}
          </Link>
        </Button>
      </div>
    );
  }

  const Icon = typeIcons[allocation.allocationType as AllocationType];
  const utilizationPercent = allocation.totalUnits > 0
    ? Math.round((allocation.consumedUnits / allocation.totalUnits) * 100)
    : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/travel/allocations">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-bold">{allocation.name}</h1>
            <p className="text-muted-foreground">
              {allocation.partner.companyName}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setIsEditModalOpen(true)}>
            <Pencil className="mr-2 h-4 w-4" />
            {tCommon('edit')}
          </Button>
          <Button variant="destructive" onClick={() => setDeleteConfirmOpen(true)}>
            <Trash2 className="mr-2 h-4 w-4" />
            {tCommon('delete')}
          </Button>
        </div>
      </div>

      {/* Modals */}
      <AllocationModal
        open={isEditModalOpen}
        onOpenChange={setIsEditModalOpen}
        editId={id}
        onSuccess={() => utils.allocation.byId.invalidate(id)}
      />

      <ConfirmationDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        title={t('dialogs.delete.title')}
        description={t('dialogs.delete.description')}
        confirmLabel={tCommon('delete')}
        variant="destructive"
        onConfirm={confirmDelete}
        isLoading={deleteMutation.isPending}
      />

      {/* Main Content Grid */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Left Column - Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Overview Card */}
          <div className="border border-border bg-card p-6">
            <div className="flex items-center gap-4">
              <div className={`flex h-12 w-12 items-center justify-center ${typeColors[allocation.allocationType as AllocationType]}`}>
                <Icon className="h-7 w-7" />
              </div>
              <div className="flex-1">
                <div className="flex flex-wrap gap-2 mb-2">
                  <span className={`px-2 py-0.5 text-xs font-medium ${typeColors[allocation.allocationType as AllocationType]}`}>
                    {t(`types.${allocation.allocationType === 'HOTEL_ROOM' ? 'hotelRoom' : allocation.allocationType === 'TOUR_SEAT' ? 'tourSeat' : allocation.allocationType === 'TRANSFER_VEHICLE' ? 'transferVehicle' : 'flightSeat'}`)}
                  </span>
                  <span className={`px-2 py-0.5 text-xs font-medium ${statusColors[allocation.status as AllocationStatus]}`}>
                    {t(`status.${allocation.status === 'FULLY_CONSUMED' ? 'fullyConsumed' : allocation.status.toLowerCase()}`)}
                  </span>
                </div>
                {allocation.reference && (
                  <p className="text-sm text-muted-foreground">
                    {t('fields.reference')}: {allocation.reference}
                  </p>
                )}
              </div>
            </div>

            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <div className="bg-muted/50 p-4">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Calendar className="h-4 w-4" />
                  <span className="text-sm">{t('fields.validityPeriod')}</span>
                </div>
                <p className="mt-1 font-medium">
                  {format(new Date(allocation.validFrom), 'MMM d, yyyy')} - {format(new Date(allocation.validTo), 'MMM d, yyyy')}
                </p>
              </div>

              <div className="bg-muted/50 p-4">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Building2 className="h-4 w-4" />
                  <span className="text-sm">{t('fields.partner')}</span>
                </div>
                <p className="mt-1 font-medium">
                  <Link to={`/travel/cari/accounts/partner/${allocation.partner.id}`} className="hover:underline">
                    {allocation.partner.companyName}
                  </Link>
                </p>
              </div>
            </div>

            {allocation.notes && (
              <div className="mt-4 border border-border p-4">
                <p className="text-sm font-medium text-muted-foreground">{t('fields.notes')}</p>
                <p className="mt-1 text-sm">{allocation.notes}</p>
              </div>
            )}
          </div>

          {/* Consumption History */}
          <div className="border border-border bg-card p-6">
            <h2 className="text-lg font-semibold mb-4">{t('sections.consumptionHistory')}</h2>

            {allocation.consumptions.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Package className="h-8 w-8 mx-auto mb-3 opacity-50" />
                <p>{t('empty.noConsumptions')}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {allocation.consumptions.map((consumption) => (
                  <div key={consumption.id} className="flex items-center justify-between border border-border p-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">
                          {consumption.bookingItem.booking.bookingReference}
                        </span>
                        {consumption.releasedAt && (
                          <span className="bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                            {t('consumptions.released')}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {consumption.bookingItem.booking.customer.firstName} {consumption.bookingItem.booking.customer.lastName}
                        {' - '}{consumption.bookingItem.name}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        <Clock className="h-3 w-3 inline mr-1" />
                        {format(new Date(consumption.serviceDate), 'MMM d, yyyy')}
                        {consumption.endDate && ` - ${format(new Date(consumption.endDate), 'MMM d, yyyy')}`}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium">
                        {consumption.units} {t('fields.units')}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          currency: consumption.currency,
                        }).format(consumption.totalCost)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right Column - Stats */}
        <div className="space-y-6">
          {/* Utilization Card */}
          <div className="border border-border bg-card p-6">
            <h3 className="font-semibold mb-4">{t('sections.utilization')}</h3>

            <div className="text-center mb-4">
              <div className="text-4xl font-bold">{utilizationPercent}%</div>
              <p className="text-sm text-muted-foreground">{t('stats.utilized')}</p>
            </div>

            <div className="h-3 w-full bg-muted mb-4">
              <div
                className={`h-3 ${
                  utilizationPercent >= 90 ? 'bg-red-500' :
                  utilizationPercent >= 70 ? 'bg-orange-500' : 'bg-green-500'
                }`}
                style={{ width: `${utilizationPercent}%` }}
              />
            </div>

            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('fields.totalUnits')}</span>
                <span className="font-medium">{allocation.totalUnits}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('fields.consumed')}</span>
                <span className="font-medium">{allocation.consumedUnits}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('fields.available')}</span>
                <span className="font-medium text-green-600">{allocation.availableUnits}</span>
              </div>
            </div>
          </div>

          {/* Pricing Card */}
          <div className="border border-border bg-card p-6">
            <h3 className="font-semibold mb-4">{t('sections.pricing')}</h3>

            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('fields.unitCost')}</span>
                <span className="font-medium">
                  {new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: allocation.currency,
                  }).format(allocation.unitCost)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('fields.totalValue')}</span>
                <span className="font-medium">
                  {new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: allocation.currency,
                  }).format(allocation.unitCost * allocation.totalUnits)}
                </span>
              </div>
              <div className="flex justify-between border-t pt-3">
                <span className="text-muted-foreground">{t('fields.consumedValue')}</span>
                <span className="font-medium text-primary">
                  {new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: allocation.currency,
                  }).format(allocation.unitCost * allocation.consumedUnits)}
                </span>
              </div>
            </div>
          </div>

          {/* Hotel Rate Info (if applicable) */}
          {allocation.hotelRate && (
            <div className="border border-border bg-card p-6">
              <h3 className="font-semibold mb-4">{t('sections.hotelRate')}</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('hotelRate.roomType')}</span>
                  <span>{allocation.hotelRate.roomType}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('hotelRate.season')}</span>
                  <span>{allocation.hotelRate.seasonName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('hotelRate.singleRate')}</span>
                  <span>
                    {new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: allocation.hotelRate.currency,
                    }).format(allocation.hotelRate.singleRate)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('hotelRate.doubleRate')}</span>
                  <span>
                    {new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: allocation.hotelRate.currency,
                    }).format(allocation.hotelRate.doubleRate)}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
